import * as React from "react";
import { Marker } from "@react-google-maps/api";
import type { Stop } from "../../types";
import { createStopTooltipOverlay } from "./tooltips/stopTooltipOverlay";

type Props = {
  map: google.maps.Map | null;
  locationsByRoot: Record<string, Stop[]>;
  expandedRoots: Set<string>;
  activeStopId?: string | null;
  onClickStop?: (s: Stop) => void;
};

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function getStopPointLabel(s: Stop) {
  return String((s as any).markerLabel ?? (s as any).type ?? "")
    .replace(/[()]/g, "")
    .trim();
}

function getStopPointTitle(s: Stop) {
  const name = String(s.name ?? "").trim();
  const label = getStopPointLabel(s);

  if (!label) return name;

  const alreadyHasSuffix = new RegExp(`\\(${escapeRegExp(label)}\\)\\s*$`).test(
    name
  );

  return alreadyHasSuffix ? name : `${name} (${label})`;
}

function createStopLocationIcon(label: string, active: boolean) {
  const size = active ? 46 : 38;
  const fill = active ? "#BF0036" : "#800020";
  const stroke = active ? "#BF0036" : "#800020";
  const text = label || "";

  const svg = `
    <svg width="${size}" height="${size}" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M24 3C15.16 3 8 10.16 8 19c0 11.5 16 26 16 26s16-14.5 16-26C40 10.16 32.84 3 24 3Z"
        fill="${fill}"
        stroke="${stroke}"
        stroke-width="3"
      />
      <circle cx="24" cy="19" r="11" fill="${fill}" />
      <text
        x="24"
        y="24"
        text-anchor="middle"
        font-family="Arial, sans-serif"
        font-size="15"
        font-weight="700"
        fill="#FFFFFF"
      >${text}</text>
    </svg>
  `;

  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(size, size),
    anchor: new google.maps.Point(size / 2, size),
  };
}

export default function StopLocationsLayer({
  map,
  locationsByRoot,
  expandedRoots,
  activeStopId,
  onClickStop,
}: Props) {
  const tooltipRef = React.useRef<ReturnType<
    typeof createStopTooltipOverlay
  > | null>(null);

  if (!tooltipRef.current) {
    tooltipRef.current = createStopTooltipOverlay();
  }

  React.useEffect(() => {
    return () => {
      tooltipRef.current?.hide();
    };
  }, []);

  if (!expandedRoots || expandedRoots.size === 0) return null;

  const markers: JSX.Element[] = [];

  for (const rootId of Array.from(expandedRoots)) {
    const list = locationsByRoot[rootId] ?? [];
    const seenLocationIds = new Set<string>();

    for (const s of list) {
      const id = String(s.id);

      if (seenLocationIds.has(id)) {
        continue;
      }

      seenLocationIds.add(id);

      const label = getStopPointLabel(s);
      const title = getStopPointTitle(s);
      const isActive = activeStopId != null && id === String(activeStopId);

      markers.push(
        <Marker
          key={`loc-${rootId}-${id}`}
          position={{ lat: s.lat, lng: s.lng }}
          title={title}
          icon={createStopLocationIcon(label, isActive)}
          onMouseOver={() => {
            if (!map) return;
            tooltipRef.current?.show(map, { lat: s.lat, lng: s.lng }, title);
          }}
          onMouseOut={() => {
            tooltipRef.current?.hide();
          }}
          onClick={() => onClickStop?.(s)}
          zIndex={isActive ? 1800 : 1200}
        />
      );
    }
  }

  return <>{markers}</>;
}
